import React, { useContext, useEffect, useState } from 'react';
import { fetchIncomeStatement, fetchIncomeStatementGrowth } from '../api';
import IncomeStatementChart from '../components/IncomeStatementChart';
import IncomeStatementTable from '../components/IncomeStatementTable';
import { StockVisualizationContext } from '../contexts/StockVisualizationContext';
import './StockVisualization.css';
import '../styles.css';

const TABLE_VIEW = {
  ABSOLUTE: 'incomeStatement',
  GROWTH: 'incomeStatementGrowth'
};

const toChartData = (rows) => {
  if (!Array.isArray(rows)) {
    return [];
  }

  return rows
    .map((row) => ({
      fiscalYear: row.fiscalYear,
      revenue: row.revenue ?? null,
      costOfRevenue: row.costOfRevenue ?? null,
      grossProfit: row.grossProfit ?? null,
      netIncome: row.netIncome ?? null,
      ebitda: row.ebitda ?? null
    }))
    .filter((row) => row.fiscalYear)
    .sort((a, b) => Number(a.fiscalYear) - Number(b.fiscalYear));
};

export default function IncomeStatement() {
  const { selectedStock } = useContext(StockVisualizationContext);
  const [incomeStatementRows, setIncomeStatementRows] = useState([]);
  const [incomeStatementGrowthRows, setIncomeStatementGrowthRows] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [tableView, setTableView] = useState(TABLE_VIEW.ABSOLUTE);
  const [isLoading, setIsLoading] = useState(false);
  const [incomeStatementError, setIncomeStatementError] = useState('');
  const [incomeStatementGrowthError, setIncomeStatementGrowthError] = useState('');

  useEffect(() => {
    let isCancelled = false;

    const loadIncomeStatement = async () => {
      if (!selectedStock) {
        setIncomeStatementRows([]);
        setIncomeStatementGrowthRows([]);
        setChartData([]);
        setIncomeStatementError('');
        setIncomeStatementGrowthError('');
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setIncomeStatementError('');
      setIncomeStatementGrowthError('');

      const [incomeStatementResult, incomeStatementGrowthResult] = await Promise.allSettled([
        fetchIncomeStatement(selectedStock),
        fetchIncomeStatementGrowth(selectedStock)
      ]);

      if (isCancelled) {
        return;
      }

      if (incomeStatementResult.status === 'fulfilled') {
        const incomeStatement = Array.isArray(incomeStatementResult.value)
          ? incomeStatementResult.value
          : [];
        setIncomeStatementRows(incomeStatement);
        setChartData(toChartData(incomeStatement));
      } else {
        console.error('Error fetching income statement data:', incomeStatementResult.reason);
        setIncomeStatementRows([]);
        setChartData([]);
        setIncomeStatementError(`Unable to load income statement data for ${selectedStock}.`);
      }

      if (incomeStatementGrowthResult.status === 'fulfilled') {
        setIncomeStatementGrowthRows(
          Array.isArray(incomeStatementGrowthResult.value)
            ? incomeStatementGrowthResult.value
            : []
        );
      } else {
        console.error(
          'Error fetching income statement growth data:',
          incomeStatementGrowthResult.reason
        );
        setIncomeStatementGrowthRows([]);
        setIncomeStatementGrowthError(
          `Unable to load income statement growth data for ${selectedStock}.`
        );
      }

      setIsLoading(false);
    };

    loadIncomeStatement();

    return () => {
      isCancelled = true;
    };
  }, [selectedStock]);

  const activeTableRows =
    tableView === TABLE_VIEW.GROWTH ? incomeStatementGrowthRows : incomeStatementRows;
  const activeTableError =
    tableView === TABLE_VIEW.GROWTH
      ? incomeStatementGrowthError
      : incomeStatementError;

  return (
    <div className="App">
      <div className="main-container">
        <div className="content-area income-statement-content-area">
          <h2>Income Statement</h2>
          <p>Selected stock: {selectedStock}</p>
          <div className="income-statement-page-section">
            {isLoading ? <p>Loading income statement data...</p> : null}
            {!isLoading && incomeStatementError ? <p>{incomeStatementError}</p> : null}
            {!isLoading && !incomeStatementError ? (
              <div className="income-statement-chart-section">
                <IncomeStatementChart chartData={chartData} selectedStock={selectedStock} />
              </div>
            ) : null}
            {!isLoading && selectedStock ? (
              <div className="income-statement-table-section">
                <div
                  className="income-statement-toggle-row"
                  role="tablist"
                  aria-label="Income statement table view"
                >
                  <div className="income-statement-toggle-group">
                    <button
                      type="button"
                      role="tab"
                      aria-selected={tableView === TABLE_VIEW.ABSOLUTE}
                      onClick={() => setTableView(TABLE_VIEW.ABSOLUTE)}
                      className={`income-statement-toggle-pill ${
                        tableView === TABLE_VIEW.ABSOLUTE ? 'is-active' : ''
                      }`}
                    >
                      Income statement
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={tableView === TABLE_VIEW.GROWTH}
                      onClick={() => setTableView(TABLE_VIEW.GROWTH)}
                      className={`income-statement-toggle-pill ${
                        tableView === TABLE_VIEW.GROWTH ? 'is-active' : ''
                      }`}
                    >
                      Income statement growth
                    </button>
                  </div>
                </div>
                {activeTableError ? <p>{activeTableError}</p> : null}
                {!activeTableError ? (
                  <IncomeStatementTable
                    rows={activeTableRows}
                    selectedStock={selectedStock}
                    tableView={tableView}
                  />
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
